import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as acceptFormActions from '~src/domains/accept/state/acceptFormReducer';
import * as styles from '~src/styles/domains/accept/AcceptPage.module.scss';
import { updateFormMeta } from '~src/utils/forms';
import { formatCustomerNames } from '~src/utils/helpers';
import { trackFocus, trackPageMount } from '~src/utils/tracking';
import { validate } from '~src/utils/validation';
import { xhrNext } from '~src/utils/xhr';
import SimpleButton from '../common/components/Button';
import CurrencyText from '../common/components/CurrencyText';
import { Endpoint, Page, Promo } from '../common/enums/api';
import { AcceptFormNames, PaymentOption } from '../common/enums/common';
import { Locale } from '../common/enums/localization';
import { setPlaidLinkToken } from '../common/state/commonReducer';
import * as commonSelectors from '../common/state/commonSelectors';
import { acceptSubPageSelector } from './state/acceptSelectors';

import OfferSelectors from './components/OfferSelectors';
import { AcceptOption } from './enums/accept';
import {
  setPaymentSelectorError,
  setAcceptSubPage,
} from './state/acceptReducer';
import {
  acceptFormSelector,
  autoPayEnabledSelector,
} from './state/acceptSelectors';
import PaymentMethods from './components/PaymentMethods';
import { AcceptSubPage } from './types/state';

const AcceptPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const acceptForm = useSelector(acceptFormSelector);
  const locale = useSelector(commonSelectors.localeSelector);
  const options = useSelector(commonSelectors.optionsSelector);
  const loanOfferSelection = useSelector(
    commonSelectors.loanOfferSelectionSelector,
  );
  const loanOffers = useSelector(commonSelectors.loanOffersSelector);
  const isOfferExpired = useSelector(commonSelectors.isOfferExpiredSelector);
  const customerNames = useSelector(commonSelectors.customerNamesSelector);
  const bankMetadata = useSelector(commonSelectors.bankMetadataSelector);
  const paymentOptions = useSelector(commonSelectors.paymentOptionsSelector);
  const selectedPaymentMethod = useSelector(
    commonSelectors.paymentMethodSelector,
  );
  const next = useSelector(commonSelectors.nextSelector);

  const plaidLinkToken = useSelector(commonSelectors.plaidLinkTokenSelector);
  const isAutoPayEnabled = useSelector(autoPayEnabledSelector);
  const isOnboardSpend = useSelector(commonSelectors.isOnboardSpendSelector);

  const subPage = useSelector(acceptSubPageSelector);

  const inputs = t('accept:inputs', {
    returnObjects: true,
  }) as acceptFormActions.AcceptLocalization;
  const bankAccountSelected =
    selectedPaymentMethod === PaymentOption.BANK_ACCOUNT;
  const isPlaidFlowCompleted = bankAccountSelected && !!bankMetadata;
  const disableSubmitButton =
    (bankAccountSelected && !bankMetadata) || isOfferExpired;
  const showCard = options.includes(AcceptOption.CARD);

  const { firstName, lastName } = formatCustomerNames(customerNames);
  const offer = loanOffers.find(offer => offer.offer_id === loanOfferSelection);
  const isCreditEnabled = paymentOptions.find(
    (option: PaymentOption) => option === PaymentOption.CREDIT,
  );
  const isAchEnabled = paymentOptions.find(
    (option: PaymentOption) => option === PaymentOption.BANK_ACCOUNT,
  );
  const showSsnField =
    inputs[AcceptFormNames.FULL_SSN] && locale === Locale.enUS;

  const hasDelayedPaymentOffers = loanOffers.some(offer =>
    offer.promos?.some(promo =>
      [
        Promo.ONE_MONTH_DELAYED,
        Promo.THREE_MONTH_DELAYED,
        Promo.SIX_MONTH_DELAYED,
        Promo.SEVEN_MONTH_DELAYED,
      ].includes(promo),
    ),
  );

  // Automatically fill in customer name on card form
  const autoFillCustomerName = () => {
    if (firstName.length && lastName.length) {
      dispatch(
        acceptFormActions.updateInputValue({
          name: AcceptFormNames.CARD_INFO_NAME,
          value: `${firstName} ${lastName}`,
        }),
      );
    }
  };

  useEffect(() => {
    batch(() => {
      dispatch(trackPageMount(Page.ACCEPT));
    });
    if (showCard) {
      autoFillCustomerName();
    }
  }, []);

  useEffect(() => {
    if (showCard) {
      autoFillCustomerName();
    }
  }, [selectedPaymentMethod]);

  useEffect(() => {
    if (plaidLinkToken) {
      dispatch(setPlaidLinkToken(null));
    }

    if (typeof inputs === 'string') {
      return;
    }

    dispatch(updateFormMeta(inputs, acceptFormActions));
  }, [locale]);

  const submitForm = () => {
    if (!selectedPaymentMethod) {
      dispatch(setPaymentSelectorError('accept:select-payment-method-error'));
      return;
    }

    /** If US Plaid, validate only the SSN input. If CA Plaid, do not validate any inputs.
     * If not Plaid flow, validate all acceptForm inputs. */
    const formToValidate = isPlaidFlowCompleted
      ? showSsnField
        ? { 'full-ssn': acceptForm['full-ssn'] }
        : {}
      : acceptForm;

    const errors = validate(formToValidate);
    /** Secure inputs are individual iframes and therefore are tricky to focus. The idea here is to find the first non-valid, non-secure input and focus that instead. */
    const firstNonSecureInputIndex = errors.findIndex(error => {
      const input = document.getElementById(error.name);
      return input?.children?.[0]?.tagName !== 'IFRAME';
    });

    errors.forEach((error, idx: number) => {
      if (idx === firstNonSecureInputIndex) {
        const errorElement = document.getElementById(error.name);
        errorElement?.focus();
      }
      dispatch(
        acceptFormActions.updateInputIsValid({
          name: error.name,
          msg: error.errorMsg,
        }),
      );
    });

    if (!errors.length) {
      /**
       * Endpoint determining logic (bankAccountSelected = Plaid):
       * CA Plaid - /accept
       * CA w/o Plaid - next
       * US Plaid - /ssn
       * US w/o Plaid - next
       */
      const isUSPlaid = bankAccountSelected && locale === Locale.enUS;
      const nextEndpoint = isUSPlaid
        ? Endpoint.SSN
        : bankAccountSelected
        ? Endpoint.ACCEPT
        : (next as Endpoint);
      dispatch(xhrNext({ forceNext: nextEndpoint }));
    } else {
      dispatch(trackFocus(['acceptForm', errors[0].name, 'value']));
    }
  };

  const continueButtonHandler = () => {
    if (subPage === AcceptSubPage.OFFERS) {
      dispatch(setAcceptSubPage(AcceptSubPage.PAYMENT_METHODS));
    } else {
      submitForm();
    }
  };

  const generateOfferSubheader = () => {
    if (!offer) return '';

    let subHeader =
      offer.pay_today > 0
        ? t('accept:subheader')
        : t(
            `accept:subheader-zero-down${
              isOnboardSpend ? '-onboard-spend' : ''
            }`,
          );

    if (hasDelayedPaymentOffers) {
      subHeader += ` ${t('accept:subheader-delayed-payments')}`;
    }

    return subHeader;
  };

  return offer ? (
    <div className={styles.accept}>
      <div className={styles['accept-header-wrapper']}>
        <div className='header-txt'>
          <Trans
            t={t}
            i18nKey={
              isOfferExpired
                ? firstName
                  ? 'accept:header-expired.name'
                  : 'accept:header-expire.no-name'
                : 'accept:header'
            }
            components={{
              payment: (
                <CurrencyText value={offer.order_amount} locale={locale} />
              ),
            }}
            values={{ name: firstName }}
          />
        </div>
        <div className={styles['accept-subheader']}>
          {generateOfferSubheader()}
        </div>
      </div>
      {subPage === AcceptSubPage.OFFERS && (
        <OfferSelectors offers={loanOffers} hideAPR />
      )}
      <div className={styles['accept-body']}>
        {subPage === AcceptSubPage.PAYMENT_METHODS && <PaymentMethods />}

        <SimpleButton
          label={t('accept:submit')}
          trackingLabel={Page.ACCEPT}
          classLabel={
            disableSubmitButton
              ? `btn-primary ${styles['accept-form-submit']} disabled`
              : `btn-primary ${styles['accept-form-submit']}`
          }
          shouldTrackClick={!!selectedPaymentMethod}
          trackClickPayload={
            isCreditEnabled || isAutoPayEnabled || isAchEnabled
              ? {
                  'payment-method': selectedPaymentMethod,
                  label: t('accept:submit'),
                  autopay: isAutoPayEnabled,
                }
              : {}
          }
          onClickPost={continueButtonHandler}
          isDisabled={disableSubmitButton}
        />
      </div>
    </div>
  ) : null;
};

export default AcceptPage;
